import { useEffect, useState } from 'react';
import { apiBaseUrl } from '../authConfig.js';

interface IntegrationStatus {
  name: string;
  status: string;
  lastSyncUtc: string | null;
  lastSuccessUtc: string | null;
  failedSyncCount: number | null;
  credentialExpiresUtc: string | null;
}

const LABELS: Record<string, string> = {
  GOOGLE_ADS: 'Google Ads',
  LINKEDIN_ADS: 'LinkedIn Ads',
  HUBSPOT: 'HubSpot',
  AI_PROVIDER: 'AI Provider',
};

export function Integrations({ getToken }: { getToken: () => Promise<string | null> }): JSX.Element {
  const [rows, setRows] = useState<IntegrationStatus[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const token = await getToken();
        const response = await fetch(`${apiBaseUrl}/api/integrations/status`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!response.ok) throw new Error(`API returned ${response.status}`);
        const body = (await response.json()) as { integrations: IntegrationStatus[] };
        if (!cancelled) setRows(body.integrations);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Unknown error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  return (
    <div className="stack">
      <div className="panel">
        <h2>Integration health</h2>
        <p>Live status read from the API. An integration reports NOT CONFIGURED until its credentials exist and a sync has actually run.</p>
        {error ? (
          <p style={{ color: 'var(--negative)', marginTop: 14 }}>Could not reach the API: {error}</p>
        ) : (
          <table className="data" style={{ marginTop: 14 }}>
            <thead>
              <tr>
                <th>Integration</th>
                <th>Status</th>
                <th>Last successful sync</th>
                <th className="num">Failed syncs</th>
                <th>Credential expires</th>
              </tr>
            </thead>
            <tbody>
              {(rows ?? []).map((row) => (
                <tr key={row.name}>
                  <td>{LABELS[row.name] ?? row.name}</td>
                  <td><span className="pill warn">{row.status.replace(/_/g, ' ')}</span></td>
                  <td style={{ color: 'var(--text-faint)' }}>{row.lastSuccessUtc ?? 'NO DATA'}</td>
                  <td className="num" style={{ color: 'var(--text-faint)' }}>{row.failedSyncCount ?? 'NO DATA'}</td>
                  <td style={{ color: 'var(--text-faint)' }}>{row.credentialExpiresUtc ?? 'NO DATA'}</td>
                </tr>
              ))}
              {rows === null && !error ? (
                <tr><td colSpan={5} style={{ color: 'var(--text-faint)' }}>Loading…</td></tr>
              ) : null}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
